import React, {useState, useEffect, useMemo, useCallback, useRef} from "react";
import {inject, observer} from "mobx-react";
import {useTranslation} from "react-i18next";
import {useParams, useNavigate, useLocation} from "react-router-dom";
//local
import {Layout} from "components/UI/layout";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import SignContractPublicPageFallback from "./components/SignContractPublicPageFallback";
import {IconTrashCan} from "components/icons";
import {SIGN_CONTRACTS} from "constants/routes.const";
import {
  checkPublicToken,
  getSignContract,
  getPublicSignContract,
  updateSignContract,
  deleteSignContract,
  changeStatusSignContract,
  createSignContractMember,
  deleteSignContractMember,
  patchSignContractParticipant,
  checkEmailSignContractMember,
  getOrganizationForSignContractMember,
  getOrganizationMembersForSignContractMember,
  signSignContractMember,
  signSignPublicContractMember,
  pollHsParticipantBankIdQrCode,
  completeHsParticipantBankIdAuth,
  downloadSignContractPDF,
  downloadPublicSignContractPDF,
  changeSignOrder,
} from "./api/contracts";
import {ConfirmModal} from "components/ConfirmModal";
import SignContractHead from "./components/SignContractHead";
import SignContractData from "./components/SignContractData";
import {SignContractProcessModal} from "./components/SignContractProcessModal";
import {SignContractSignModal} from "./components/SignContractSignModal";
import {SignContractAuthenticateModal} from "./components/SignContractAuthenticateModal";
import {SignContractAddMemberModal} from "./components/SignContractAddMemberModal";
import {SignContractSigningButton} from "./components/atoms/SignContractSigningButton";
import {SignContractDraftButton} from "./components/atoms/SignContractDraftButton";
import {Preloader} from "components/Preloader";
import {showSuccess} from "helpers/notifications.helper";
import {hasPermission, scrollToTop, saveDocument} from "helpers/helper";
import {fileExtension, fileType} from "constants/file.consts";
import {LockValue} from "components/LockValue";
import {
  signContractStatus,
  signContractSide,
  signContractPartyMemberSide,
  signContractIdentifyMethod,
} from "./constants";
import {SignContractContext} from "./SignContractContext";
import {
  getPendingBankIdAuth,
  setPendingBankIdAuth,
  removePendingBankIdAuth,
} from "./helpers";
import {hasUserPermission} from "helpers/helper";
import {roles} from "constants/auth.const";
import "./styles/item.scss";
import "./styles/invoice-profile-data.scss";

const SignContractPage = inject("store")(
  observer(({store: {auth, organization, clientStore}}) => {
    const {user, clientRole} = auth;
    const {language} = clientStore;
    const navigate = useNavigate();
    const {contractId} = useParams();
    const location = useLocation();
    const {t} = useTranslation();

    const isOwner = hasUserPermission([roles.OWNER], clientRole);

    const [publicMember, setPublicMember] = useState(null);
    const [organizationMembers, setOrganizationMembers] = useState([]);
    const [contract, setContract] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingActionButton, setLoadingActionButton] = useState(false);
    const [edits, setEdits] = useState({name: false, company: false});
    const [fileDataView, setFileDataView] = useState(false);
    const [showSignContractProcessModal, setShowSignContractProcessModal] =
      useState(null);
    const [showSignContractSignModal, setShowSignContractSignModal] = useState(null);
    const [showAuthenticateModal, setShowAuthenticateModal] = useState(null);
    const [showSignContractAddMemberModal, setShowSignContractAddMemberModal] =
      useState(false);
    const [isResumingBankIdAuth, setIsResumingBankIdAuth] = useState(false);
    const isPendingBankIdAuthChecked = useRef(false);

    const isMySignContract = useMemo(
      () => contract?.createdMemberId === user?.OrganizationMemberId,
      [contract, user]
    );

    const isSignOrderUsed = useMemo(() => {
      if (!contract) return false;
      const participants = [
        ...(contract.initiatingMembers ?? []),
        ...(contract.counterpartyMembers ?? []),
      ];
      return participants.some((p) => (p.signOrder ?? 1) > 1);
    }, [contract]);
    const isExtraSignIdentify = hasPermission([
      "ENABLE_SIGN_CONTRACT_EXTRA_SIGN_IDENTIFY",
    ]);
    const isEditField = useMemo(
      () => Object.values(edits).some((edit) => edit === true),
      [edits]
    );
    const canSendForSignature = useMemo(
      () =>
        hasPermission(["CHANGE_STATUS_SIGN_CONTRACT_DRAFT_TO_SIGNING"]) ||
        isMySignContract,
      [isMySignContract]
    );

    //TODO remove support for the "token" key on 08/01/2026
    const emailToken = useMemo(() => {
      if (!location.search) return null;
      const params = Object.fromEntries(new URLSearchParams(location.search));
      return params?.token ?? params?.emailToken;
    }, [location.search]);
    const isPublic = useMemo(() => !!emailToken, [emailToken]);
    const userOrganizationName = useMemo(
      () => (publicMember ? publicMember?.companyName : user?.ActiveOrganization?.name),
      [publicMember, user]
    );

    const side = useMemo(
      () =>
        !contract?.status || contract?.status === signContractStatus.DRAFT
          ? signContractSide.BUYER
          : isMySignContract ||
            !!contract.initiatingMembers.find(
              (member) => member.OrganizationMemberId === user?.OrganizationMemberId
            )
          ? signContractSide.BUYER
          : signContractSide.SELLER,
      [contract, user, isMySignContract]
    );
    const isBuyerSide = useMemo(() => side === signContractSide.BUYER, [side]);

    const status = useMemo(
      () => ({
        isDraft: contract?.status === signContractStatus.DRAFT,
        isSigning: contract?.status === signContractStatus.SIGNING,
        isSigned: contract?.status === signContractStatus.SIGNED,
      }),
      [contract]
    );

    const isEnabledActionButton = useMemo(
      () =>
        !!contract?.files?.contracts?.length &&
        contract?.initiatingMembers?.length &&
        contract?.counterpartyMembers?.length,
      [contract]
    );

    const getContract = useCallback(async () => {
      await (isPublic
        ? checkPublicToken({emailToken})
            .then(setPublicMember)
            .then(async () => await getPublicSignContract(contractId, emailToken))
        : getSignContract(contractId)
      )
        .then(setContract)
        .catch(() => !isPublic && navigate(SIGN_CONTRACTS))
        .finally(() => setLoading(false));
    }, [contractId, navigate, emailToken, isPublic]);

    const handleChange = async (field, value, action) => {
      if (!field && !value && action === "refresh") return await getContract();
      if (action === "update")
        return await updateSignContract(contractId, {...value}).then(setContract);
      if (field === signContractPartyMemberSide.OWN) {
        if (action === "add") {
          await createSignContractMember(contract.id, {
            OrganizationMemberId: value?.[0],
            isInitiatingSide: isBuyerSide,
          });
        }
        if (action === "remove") await deleteSignContractMember(contract.id, value);
        return await getContract();
      }
      if (field === signContractPartyMemberSide.THEIR) {
        if (action === "add") return setShowSignContractAddMemberModal(true);
        if (action === "remove") await deleteSignContractMember(contract.id, value);
        return await getContract();
      }
      if (field === "status") return setShowSignContractProcessModal(value);
      if (field === "memberSign") {
        const {identifyMethod} = value;
        if (!identifyMethod) return;
        return setShowSignContractSignModal({...value});
      }
      if (field === "signOrder") {
        const {participantId, signOrder} = value;
        await changeSignOrder(contract.id, participantId, signOrder);
        return await getContract();
      }
      if (field === "identifyMethod") {
        if (!isExtraSignIdentify) return;
        const {id: participantId, identifyMethod} = value;
        await patchSignContractParticipant(contract.id, participantId, {identifyMethod});
        return await getContract();
      }
      return await updateSignContract(contractId, {[field]: value}).then(
        (data) => action === "refresh" && setContract(data)
      );
    };

    const handleSignModalAccept = () => {
      removePendingBankIdAuth(contract.id);
      showSignContractSignModal.identifyMethod === signContractIdentifyMethod.BASIC_AUTH
        ? memberSignContact(showSignContractSignModal.isLastSigningMember)
        : setShowAuthenticateModal(showSignContractSignModal);
      setShowSignContractSignModal(null);
    };

    const handleSignAuthenticate = () => {
      removePendingBankIdAuth(contract.id);
      memberSignContact(showAuthenticateModal?.isLastSigningMember);
      setShowAuthenticateModal(null);
    };

    const handleCloseAuthenticate = async () => {
      removePendingBankIdAuth(contract.id);
      setShowAuthenticateModal(null);
      await getContract();
    };

    const changeStatus = async (nextStatus) => {
      setShowSignContractProcessModal(null);
      await changeStatusSignContract(contractId, nextStatus);
      showSuccess(t(`SIGN_CONTRACT_NOTIFICATION_TO_${nextStatus}`));
      await getContract();
    };

    const memberSignContact = useCallback(
      async (isLastSigningMember) => {
        isPublic
          ? await signSignPublicContractMember(contractId, emailToken)
          : await signSignContractMember(contractId);
        showSuccess(t("SIGN_CONTRACT_NOTIFICATION_MEMBER_SIGNED"));
        await getContract();
        isLastSigningMember && !isPublic && auth.checkToken(true);
      },
      [auth, contractId, emailToken, getContract, isPublic, t]
    );

    const handleDeleteContract = () =>
      ConfirmModal({
        title: t("SIGN_CONTRACT_DELETE_MODAL_TITLE"),
        text: t("SIGN_CONTRACT_DELETE_MODAL_TEXT"),
        type: "warning",
        noQuestionSymbol: true,
        onOk: () => {
          setLoadingActionButton(true);
          deleteSignContract(contractId)
            .then(() => {
              auth.checkToken(true);
              navigate(SIGN_CONTRACTS);
              showSuccess(t("SIGN_CONTRACT_NOTIFICATION_DELETED"));
            })
            .catch(() => setLoadingActionButton(false));
        },
      });

    const handleAddContractMember = async (member) => {
      await createSignContractMember(contract.id, member);
      setShowSignContractAddMemberModal(false);
      await getContract();
    };

    const handleDownloadContract = async () => {
      setLoadingActionButton(true);

      try {
        const contractName = `${(
          contract.name ?? t("SIGN_CONTRACT_UNNAMED_LABEL")
        ).replace(/\.pdf$/i, "")}`;
        const extension = contract.isZipFileReady ? "zip" : "pdf";
        const fileName = `${contract.number}・${contractName}.${extension}`;
        const data = isPublic
          ? await downloadPublicSignContractPDF(contract?.id, emailToken)
          : await downloadSignContractPDF(contract?.id);
        if (!data) return handleChange(undefined, undefined, "refresh");

        const response = await fetch(data.url);
        const fileData = await response.blob();
        const fileExt = contract.isZipFileReady ? fileExtension.ZIP : fileExtension.PDF;
        saveDocument(fileData, fileType[fileExt], fileName);
      } finally {
        setLoadingActionButton(false);
      }
    };

    const resumeBankIdAuth = useCallback(
      async (pending) => {
        const {orderRef, participantId, isLastSigningMember, identifyMethod} = pending;
        setIsResumingBankIdAuth(true);
        try {
          const data = await pollHsParticipantBankIdQrCode(
            contractId,
            participantId,
            emailToken,
            {orderRef}
          );
          if (!data.completionData) {
            if (data.status !== "failed")
              setShowAuthenticateModal({
                participantId,
                isLastSigningMember,
                identifyMethod,
              });
            return;
          }
          await completeHsParticipantBankIdAuth(contractId, participantId, emailToken, {
            completionData: data.completionData,
          });
          await memberSignContact(isLastSigningMember);
        } catch (_e) {
        } finally {
          setIsResumingBankIdAuth(false);
        }
      },
      [contractId, emailToken, memberSignContact]
    );

    useEffect(() => {
      getContract();
      scrollToTop();
    }, [getContract]);

    useEffect(() => {
      !isPublic &&
        organization.getOrganizationMembers(undefined, true).then(setOrganizationMembers);
    }, [organization, isPublic]);

    useEffect(() => {
      if (!contract || isPendingBankIdAuthChecked.current) return;
      isPendingBankIdAuthChecked.current = true;
      const pendingBankIdAuth = getPendingBankIdAuth(contract.id);
      if (!pendingBankIdAuth) return;
      removePendingBankIdAuth(contract.id);
      resumeBankIdAuth(pendingBankIdAuth);
    }, [contract, resumeBankIdAuth]);

    return (
      <SignContractContext.Provider
        value={{
          user: publicMember ?? user,
          language,
          organizationMembers,
          side,
          status,
          isPublic,
          isEditField,
          canSendForSignature,
          isMySignContract,
          setEdits: (value) => setEdits((prevState) => ({...prevState, ...value})),
        }}
      >
        <Layout>
          <div className="signing_contract__wrapper">
            {(loading || isResumingBankIdAuth) && <Preloader addClass="preloader500" />}
            {!loading && isPublic && !contract && <SignContractPublicPageFallback />}
            {!loading && !isResumingBankIdAuth && contract && (
              <React.Fragment>
                <SignContractHead
                  contract={contract}
                  loading={loadingActionButton}
                  fileDataView={fileDataView}
                  onChange={handleChange}
                  onDownload={handleDownloadContract}
                />
                <SignContractData
                  contract={contract}
                  isOwner={isOwner}
                  isSignOrderUsed={isSignOrderUsed}
                  fileDataView={fileDataView}
                  onChangeFileDataView={setFileDataView}
                  onChange={handleChange}
                />
                {isBuyerSide && (
                  <div className="df-row-center mt-8">
                    {status.isDraft && (
                      <ButtonTransparent
                        name="SIGN_CONTRACT_ACTION_BUTTON_DELETE_DRAFT"
                        small
                        color="red"
                        icon={<IconTrashCan />}
                        loading={loadingActionButton}
                        onClick={handleDeleteContract}
                        addClass="mla"
                      />
                    )}
                    {status.isDraft && (
                      <LockValue
                        canPermissions={["CHANGE_STATUS_SIGN_CONTRACT_DRAFT_TO_SIGNING"]}
                        additionalShowCondition={canSendForSignature}
                        needLabel
                        needLeftIcon
                        addClassIcon="ml-4"
                      >
                        <SignContractSigningButton
                          loading={loadingActionButton}
                          disabled={!isEnabledActionButton || isEditField}
                          disabledTooltip={
                            isEditField
                              ? "SIGN_CONTRACT_ACTION_BUTTON_TO_SIGNING_EDIT_DISABLED"
                              : undefined
                          }
                          onClick={
                            canSendForSignature
                              ? () => handleChange("status", signContractStatus.SIGNING)
                              : undefined
                          }
                          addClass={canSendForSignature ? "ml-4" : "ml-1"}
                        />
                      </LockValue>
                    )}
                    {status.isSigning && (
                      <SignContractDraftButton
                        loading={loadingActionButton}
                        onClick={() => handleChange("status", signContractStatus.DRAFT)}
                      />
                    )}
                  </div>
                )}
                {!!showSignContractProcessModal && (
                  <SignContractProcessModal
                    status={showSignContractProcessModal}
                    onAccept={changeStatus}
                    onClose={() => setShowSignContractProcessModal(null)}
                  />
                )}
                {!!showSignContractSignModal && (
                  <SignContractSignModal
                    file={contract.files?.contracts?.[0] ?? null}
                    organizationName={userOrganizationName}
                    lastSignature={showSignContractSignModal.isLastSigningMember}
                    onAccept={handleSignModalAccept}
                    onClose={async () => {
                      setShowSignContractSignModal(null);
                      await getContract();
                    }}
                  />
                )}
                {!!showAuthenticateModal && (
                  <SignContractAuthenticateModal
                    contractId={contract.id}
                    participantId={showAuthenticateModal.participantId}
                    emailToken={emailToken}
                    identifyMethod={showAuthenticateModal.identifyMethod}
                    onOpenApp={(orderRef) => {
                      const values = {...showAuthenticateModal, orderRef};
                      setPendingBankIdAuth(contract.id, values);
                    }}
                    onAuthenticated={handleSignAuthenticate}
                    onClose={handleCloseAuthenticate}
                  />
                )}

                {!!showSignContractAddMemberModal && (
                  <SignContractAddMemberModal
                    onSearchCompany={getOrganizationForSignContractMember}
                    onSearchMembers={getOrganizationMembersForSignContractMember}
                    onSeacrhEmail={checkEmailSignContractMember}
                    onAccept={handleAddContractMember}
                    onClose={() => setShowSignContractAddMemberModal(false)}
                  />
                )}
              </React.Fragment>
            )}
          </div>
        </Layout>
      </SignContractContext.Provider>
    );
  })
);
export default SignContractPage;
